import React, { forwardRef, useEffect, useImperativeHandle } from 'react'
import {
  Control,
  ControllerRenderProps,
  FieldValues,
  useForm,
  UseFormGetValues,
} from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import { INPUT_TYPES } from '../input-type-text/INPUT_TYPES'
import InputTypeText, {
  ITextInputProps,
} from '../input-type-text/input-type-text'
import { Button } from '../../atoms'

type RevampAdultsPickerContentProps = {
  label?: React.ReactNode
  inputTextProps: Pick<
    ITextInputProps,
    'placeholder' | 'inputContainerClassName' | 'className' | 'formatValue'
  >
  disabled: boolean
  onSubmit: () => void
  isMobile: boolean
  control: Control<
    {
      number: string
    },
    any
  >
  getValues: UseFormGetValues<{
    number: string
  }>
}

type AdultsPickerContainerRef = {
  onChange: () => void
}

type AdultsPickerContainerProps = {
  label?: React.ReactNode
  inputTextProps: Pick<
    ITextInputProps,
    'placeholder' | 'inputContainerClassName' | 'className' | 'formatValue'
  >
  onClose?: () => void
  onIsAdultSelected?: (value: boolean) => void
  onChange?: (value: number | undefined) => void
  onValid?: () => void
  field: ControllerRenderProps<FieldValues, string>
  isMobile: boolean
}

export const RevampAdultsPickerContent: React.FC<
  RevampAdultsPickerContentProps
> = ({
  label,
  inputTextProps,
  onSubmit,
  isMobile,
  control,
  getValues,
  disabled,
}) => {
  return (
    <form onSubmit={onSubmit}>
      <label className='text-sm' data-testid='adults-picker'>
        {label}
        <InputTypeText
          name='number'
          control={control}
          inputType={INPUT_TYPES.INT}
          rules={{}}
          data-focus
          value={getValues('number')}
          {...inputTextProps}
        />
      </label>

      {!isMobile && (
        <div className='flex justify-end pt-4'>
          <Button
            className='cursor-pointer rounded-[4px] px-4 py-[10px] text-sm leading-none'
            type='submit'
            disabled={disabled}
          >
            <FormattedMessage
              id='ui.AdultsPicker.save'
              defaultMessage='Enregistrer'
            />
          </Button>
        </div>
      )}
    </form>
  )
}

export const RevampAdultsPickerContainer = forwardRef<
  AdultsPickerContainerRef,
  AdultsPickerContainerProps
>(
  (
    {
      field,
      onClose,
      onIsAdultSelected,
      onChange,
      onValid,
      isMobile,
      ...contentProps
    },
    refParent,
  ) => {
    const {
      control,
      handleSubmit: _handleSubmit,
      formState: { isValid },
      getValues,
      watch,
    } = useForm<{ number: string }>({
      mode: 'onChange',
      defaultValues: {
        number: field?.value,
      },
    })
    const value = watch('number')
    const formatedValue = value ? Number(value) : 0
    const formIsNotValid = !isValid || !formatedValue || formatedValue <= 0

    const onSubmit = (number: string) => {
      const formattedValue = number ? Number(number) : 0
      if (formattedValue <= 0) {
        return
      }
      field.onChange(formattedValue)
      onIsAdultSelected?.(true)
      onValid?.()
      onClose?.()
    }

    useImperativeHandle(
      refParent,
      () => ({
        onChange: () => {
          if (!isMobile) {
            if (formatedValue > 0) {
              field.onChange(formatedValue)
            }
          }
        },
      }),
      [formatedValue, isMobile, onValid],
    )

    const handleSubmit = _handleSubmit((data) => {
      onSubmit(data.number)
    })

    useEffect(() => {
      const subscription = watch(({ number }) => {
        if (isMobile) {
          const formatedValue = number ? Number(number) : 0
          if (formatedValue > 0) {
            onChange?.(formatedValue)
          } else {
            onChange?.(undefined)
          }
        }
      })

      return () => subscription.unsubscribe()
    }, [watch])

    return (
      <RevampAdultsPickerContent
        control={control}
        onSubmit={handleSubmit}
        isMobile={isMobile}
        disabled={formIsNotValid}
        getValues={getValues}
        {...contentProps}
      />
    )
  },
)
